import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  getLandingArchiveIndex2ContentWidth,
  type LandingArchiveCandidate,
} from '../shared/util/landingArchiveIndex';
import { useDeviceStore } from '../shared/store/deviceStore';
import { DeviceType } from '../shared/enum/EnumRepository';
import { useExhibitionVenue } from '../shared/hooks/useApi/useExhibitionVenue';
import {
  buildExhibitionVenueIndexColumns,
  buildExhibitionVenueIndexGroups,
  EXHIBITION_VENUE_LAYOUT_GAP,
  INTEREST_EXHIBITION_CONTAINER_MAX_WIDTH,
  INTEREST_EXHIBITION_CONTAINER_MIN_WIDTH,
  LANDING_COLUMN_GAP2,
  getExhibitionVenueIndexMaxColumns,
} from '../shared/util/exhibitionVenueIndex';
import ExhibitionMetaStation from '../component/exhibitions/ExhibitionMetaStation';
import ExhibitionRightBottomIsland from '../component/exhibitions/ExhibitionRightBottomIsland';
import ExhibitionVenueIndex from '../component/exhibitions/ExhibitionVenueIndex';
import InterestExhibitionContainer from '../component/exhibitions/InterestExhibitionContainer';
import ExhibitionMetaStationMobile from '../component/exhibitions/mobile/ExhibitionMetaStationMobile';
import ExhibitionVenueIndexMobile from '../component/exhibitions/mobile/ExhibitionVenueIndexMobile';
import { getBtnName } from '../shared/constants/btnNames';
import { getPageMessage } from '../shared/constants/pageMessages';
import { useLanguageStore } from '../shared/store/languageStore';

/** WorkList → WorkViewer와 같은 너비 전환 */
const INTEREST_PANEL_MS = 700;

const Exhibitions: React.FC = () => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const language = useLanguageStore((s) => s.language);
  const venueAreaRef = useRef<HTMLDivElement>(null);
  const [venueAreaWidth, setVenueAreaWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [isInterestPanelOpen, setIsInterestPanelOpen] = useState(false);
  const [keepInterestMounted, setKeepInterestMounted] = useState(false);
  const {
    venueList,
    interestExhibitions,
    loadingVenueIds,
    interestedVenueIds,
    loadVenues,
    addInterestExhibitions,
    removeInterestVenue,
    setVenueLoading,
  } = useExhibitionVenue();
  const hasInterestPanel = interestExhibitions.length > 0 || loadingVenueIds.length > 0;
  const selectedVenueCount = useMemo(
    () => new Set([...interestedVenueIds, ...loadingVenueIds]).size,
    [interestedVenueIds, loadingVenueIds],
  );
  const activeVenueIds = useMemo(
    () => new Set([...interestedVenueIds, ...loadingVenueIds]),
    [interestedVenueIds, loadingVenueIds],
  );

  useEffect(() => {
    void loadVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedVenueCount === 0) setIsInterestPanelOpen(false);
  }, [selectedVenueCount]);

  useEffect(() => {
    if (isInterestPanelOpen) {
      setKeepInterestMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setKeepInterestMounted(false), INTEREST_PANEL_MS);
    return () => window.clearTimeout(timer);
  }, [isInterestPanelOpen]);

  useLayoutEffect(() => {
    const venueArea = venueAreaRef.current;
    if (!venueArea) return;

    const measure = () => {
      setVenueAreaWidth(venueArea.clientWidth);
      setViewportWidth(window.innerWidth);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(venueArea);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isMobile]);

  const desktopMaxColumns = useMemo(
    () => getExhibitionVenueIndexMaxColumns(
      venueAreaWidth,
      viewportWidth,
      hasInterestPanel,
    ),
    [venueAreaWidth, viewportWidth, hasInterestPanel],
  );

  const indexColumns = useMemo(
    () => buildExhibitionVenueIndexColumns(venueList, {
      language,
      maxColumns: desktopMaxColumns,
    }),
    [venueList, language, desktopMaxColumns],
  );

  const indexGroups = useMemo(
    () => buildExhibitionVenueIndexGroups(venueList, language),
    [venueList, language],
  );

  const handleVenueActivate = useCallback(
    (candidate: LandingArchiveCandidate) => {
      const venueId = candidate.aui?.trim();
      if (!venueId) return;
      if (activeVenueIds.has(venueId)) {
        removeInterestVenue(venueId);
        setVenueLoading(venueId, false);
        return;
      }
      void addInterestExhibitions(venueId);
    },
    [activeVenueIds, addInterestExhibitions, removeInterestVenue, setVenueLoading],
  );

  const toggleInterestPanel = () => {
    setIsInterestPanelOpen((open) => !open);
  };

  if (isMobile) {
    return (
      <ExhibitionsMobileContainer>
        <ExhibitionRightBottomIsland fixed />
        <MobileFrame>
          <MobileSlide>
            <MobileIndexArea $isPanelOpen={isInterestPanelOpen}>
              <IndexMeta $reserveButton={selectedVenueCount > 0}>
                <ExhibitionMetaStationMobile />
              </IndexMeta>
              <IndexList>
                {indexGroups.length === 0 ? (
                  <StatusMessage>{getPageMessage('EXHIBITION_NO_MATCHING_RESULTS')}</StatusMessage>
                ) : (
                  <ExhibitionVenueIndexMobile
                    groups={indexGroups}
                    activeVenueIds={activeVenueIds}
                    onVenueActivate={handleVenueActivate}
                  />
                )}
              </IndexList>
            </MobileIndexArea>
            {(isInterestPanelOpen || keepInterestMounted) && (
              <MobileInterestArea>
                <InterestExhibitionContainer />
              </MobileInterestArea>
            )}
          </MobileSlide>
          {selectedVenueCount > 0 && (
            <VenuePanelBtn
              type="button"
              $isPanelOpen={isInterestPanelOpen}
              aria-expanded={isInterestPanelOpen}
              onClick={toggleInterestPanel}
            >
              {isInterestPanelOpen
                ? getBtnName('EXHIBITION_VENUE_BACK')
                : getBtnName('EXHIBITION_SELECTED_VENUES', { count: selectedVenueCount })}
            </VenuePanelBtn>
          )}
        </MobileFrame>
      </ExhibitionsMobileContainer>
    );
  }

  return (
    <ExhibitionsContainer>
      <ContentRow>
        <LeftColumn>
          <ExhibitionMetaStation />
        </LeftColumn>
        <VenueArea ref={venueAreaRef}>
          <IndexFrame $columnCount={Math.max(1, indexColumns.length)}>
            <IndexArea>
              {indexColumns.length === 0 ? (
                <StatusMessage>{getPageMessage('EXHIBITION_NO_MATCHING_RESULTS')}</StatusMessage>
              ) : (
                <ExhibitionVenueIndex
                  columns={indexColumns}
                  activeVenueIds={activeVenueIds}
                  onVenueActivate={handleVenueActivate}
                />
              )}
            </IndexArea>
            <RequestSlot
              $left={indexColumns.length === 0
                ? '0px'
                : `calc(${getLandingArchiveIndex2ContentWidth(indexColumns.length, false)} + ${LANDING_COLUMN_GAP2})`}
            >
              <ExhibitionRightBottomIsland />
            </RequestSlot>
          </IndexFrame>
          {hasInterestPanel && (
            <InterestArea onClick={(e) => e.stopPropagation()}>
              <InterestExhibitionContainer />
            </InterestArea>
          )}
        </VenueArea>
      </ContentRow>
    </ExhibitionsContainer>
  );
};

/** Header/floating island clearance. Index uses this as scrollable padding so content can reach viewport top. */
const EXHIBITIONS_TOP_OFFSET = 'calc(clamp(100px, 12vh, 140px) + clamp(12px, 2vh, 24px))';

const ExhibitionsContainer = styled.main`
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const ExhibitionsMobileContainer = styled.main`
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: clamp(80px, 20vh, 90px) clamp(20px, 4vw, 30px) clamp(24px, 4vh, 48px);
`;

const ContentRow = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  overflow: hidden;
  padding: 0 clamp(20px, 4vw, 30px) clamp(24px, 4vh, 48px);
  box-sizing: border-box;
  gap: 10px;
`;

const LeftColumn = styled.div`
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;
`;

const VenueArea = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  display: flex;
  gap: ${EXHIBITION_VENUE_LAYOUT_GAP}px;
  overflow: hidden;
`;

const IndexFrame = styled.div<{ $columnCount: number }>`
  position: relative;
  flex: 1 0 ${({ $columnCount }) =>
    getLandingArchiveIndex2ContentWidth($columnCount, false)};
  min-width: ${({ $columnCount }) =>
    getLandingArchiveIndex2ContentWidth($columnCount, false)};
  height: 100%;
  min-height: 0;
`;

const IndexArea = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const RequestSlot = styled.div<{ $left: string }>`
  position: absolute;
  top: ${EXHIBITIONS_TOP_OFFSET};
  left: ${({ $left }) => $left};
  z-index: 2;
`;

const MobileFrame = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const VenuePanelBtn = styled.button<{ $isPanelOpen: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $isPanelOpen }) => ($isPanelOpen ? '0%' : '100%')};
  z-index: 3;
  margin: 0;
  padding: 0 0 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transform: ${({ $isPanelOpen }) => ($isPanelOpen ? 'translateX(0)' : 'translateX(-100%)')};
  transition:
    left ${INTEREST_PANEL_MS}ms ease-in-out,
    transform ${INTEREST_PANEL_MS}ms ease-in-out;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobileSlide = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  overflow: hidden;
`;

const MobileIndexArea = styled.div<{ $isPanelOpen: boolean }>`
  flex: 0 0 auto;
  width: ${({ $isPanelOpen }) => ($isPanelOpen ? '0%' : '100%')};
  max-width: ${({ $isPanelOpen }) => ($isPanelOpen ? '0%' : '100%')};
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: ${({ $isPanelOpen }) => ($isPanelOpen ? 0 : 1)};
  overflow: hidden;
  pointer-events: ${({ $isPanelOpen }) => ($isPanelOpen ? 'none' : 'auto')};
  transition: all ${INTEREST_PANEL_MS}ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const IndexMeta = styled.div<{ $reserveButton: boolean }>`
  flex-shrink: 0;
  margin-bottom: 4px;
  padding-right: ${({ $reserveButton }) => ($reserveButton ? '7.5rem' : '0')};
  box-sizing: border-box;
`;

const IndexList = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const MobileInterestArea = styled.div`
  flex: 0 0 auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding-top: 22px;
  box-sizing: border-box;
  overflow: hidden;
`;

const InterestArea = styled.div`
  flex: 0 1 ${INTEREST_EXHIBITION_CONTAINER_MAX_WIDTH}px;
  width: clamp(
    ${INTEREST_EXHIBITION_CONTAINER_MIN_WIDTH}px,
    29vw,
    ${INTEREST_EXHIBITION_CONTAINER_MAX_WIDTH}px
  );
  min-width: ${INTEREST_EXHIBITION_CONTAINER_MIN_WIDTH}px;
  max-width: ${INTEREST_EXHIBITION_CONTAINER_MAX_WIDTH}px;
  height: 100%;
  min-height: 0;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;
`;

const StatusMessage = styled.div`
  width: 100%;
  padding-top: 3px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

export default Exhibitions;
